import { buildSupportMailtoHref } from "@/utils/supportEmail"

import { NEAR_LIMIT_THRESHOLD } from "../constants"
import type { SubscriptionResponse, UsageResponse } from "../types"
import { canonicalTier } from "./tierLabel"

/**
 * Rungs of the usage ladder, ordered by severity. Every surface that says
 * something about usage reads its band from here, so the banner, the usage page
 * and the launcher cannot describe the same organization differently.
 *
 * A band is derived from current usage on render — it is a state, not an event.
 * Nothing records that an organization crossed a rung, so nothing here can claim
 * an organization was told.
 */
export type UsageBand = "approaching" | "close" | "nearly" | "over"

type UsageDimension = "simulation_minutes" | "conversation_uploads"

export interface UsageBandState {
  band: UsageBand
  dimension: UsageDimension
  /** Fraction of the allowance used. At or above 1 on the `over` band. */
  ratio: number
  used: number
  limit: number
}

interface CapacityAction {
  label: string
  /** Set for a tier with no self-serve capacity path; opens mail instead of the portal. */
  href?: string
}

const BAND_FLOORS: ReadonlyArray<readonly [UsageBand, number]> = [
  ["over", 1],
  ["nearly", 0.98],
  ["close", 0.9],
  ["approaching", NEAR_LIMIT_THRESHOLD],
]

const SEVERITY: Record<UsageBand, number> = {
  approaching: 0,
  close: 1,
  nearly: 2,
  over: 3,
}

const DIMENSION_LABEL: Record<UsageDimension, string> = {
  simulation_minutes: "simulation minutes",
  conversation_uploads: "conversation uploads",
}

// Only the self-serve tiers are banded. Enterprise and partner organizations
// resolve their quota elsewhere, so a percentage against the limit in this
// response would be measured against the wrong denominator. Tiers are compared
// after normalizing, so a legacy `platform` or `scale` organization bands as the
// Starter or Growth org it is.
const BANDED_TIERS = new Set<SubscriptionResponse["tier"]>([
  "starter",
  "growth",
])

function bandForRatio(ratio: number): UsageBand | null {
  for (const [band, floor] of BAND_FLOORS) {
    if (ratio >= floor) {
      return band
    }
  }
  return null
}

/**
 * Fraction of an allowance used, or null when there is no allowance to measure
 * against. A null limit is unlimited, not zero. A zero limit is a real (zeroed)
 * allowance, which only reads as spent once something has been used against it.
 */
function usageRatio(used: number, limit: number | null): number | null {
  if (typeof limit !== "number" || limit < 0) {
    return null
  }
  if (limit === 0) {
    return used > 0 ? Number.POSITIVE_INFINITY : null
  }
  return used / limit
}

/**
 * The rung a single dimension sits on, from its own numbers. Unlike
 * `usageBandState` this makes no policy judgement about the organization — it is
 * what a meter uses to colour itself, including for a tier the ladder excludes.
 */
export function usageBandFor(
  used: number,
  limit: number | null,
): UsageBand | null {
  const ratio = usageRatio(used, limit)
  return ratio === null ? null : bandForRatio(ratio)
}

function stateFor(
  dimension: UsageDimension,
  used: number,
  limit: number | null,
): UsageBandState | null {
  const ratio = usageRatio(used, limit)
  if (ratio === null || typeof limit !== "number") {
    return null
  }
  const band = bandForRatio(ratio)
  return band === null ? null : { band, dimension, ratio, used, limit }
}

/**
 * The most severe band an organization is on, across every limited dimension,
 * or null when it is below the first rung on all of them. Minutes win a tie:
 * they are the dimension a self-serve plan is actually sized by.
 *
 * Billing source is not consulted: how an organization is invoiced does not
 * change where it stands against its allowance. Which rungs a surface acts on is
 * that surface's call.
 */
export function usageBandState(
  subscription: SubscriptionResponse | null,
  usage: UsageResponse | null | undefined,
): UsageBandState | null {
  if (!usage || subscription === null) {
    return null
  }
  if (!BANDED_TIERS.has(canonicalTier(subscription.tier))) {
    return null
  }

  const candidates = [
    stateFor(
      "simulation_minutes",
      usage.simulation_minutes_used,
      usage.simulation_minutes_limit,
    ),
    stateFor(
      "conversation_uploads",
      usage.conversation_uploads_count,
      usage.conversation_uploads_limit,
    ),
  ].filter((state): state is UsageBandState => state !== null)

  return candidates.reduce<UsageBandState | null>((worst, candidate) => {
    if (worst === null) {
      return candidate
    }
    return SEVERITY[candidate.band] > SEVERITY[worst.band] ? candidate : worst
  }, null)
}

type CapacityContext = Pick<SubscriptionResponse, "tier" | "billing_source">

/**
 * Whether this organization can add capacity itself. Only a Stripe-billed
 * Starter can: Growth has no self-serve step above it, and an organization
 * invoiced another way has no portal to change.
 */
function hasSelfServeCapacity(subscription: CapacityContext): boolean {
  return (
    subscription.billing_source === "stripe" &&
    canonicalTier(subscription.tier) === "starter"
  )
}

/**
 * Whether the usage ladder's warning rungs describe this organization. The rungs
 * are an offer of capacity, so they are held to the organizations that have a
 * self-serve one to take. Being out of allowance is a fact rather than an offer,
 * so that rung is said whatever the billing source — see the callers, which read
 * this alongside the band.
 */
export function usageLadderApplies(subscription: CapacityContext): boolean {
  return (
    subscription.billing_source === "stripe" &&
    BANDED_TIERS.has(canonicalTier(subscription.tier))
  )
}

/**
 * The capacity path offered at every rung. Where there is no self-serve
 * mechanism the offer is a conversation, rather than a button that resolves to
 * nothing.
 */
export function usageCapacityAction(
  subscription: CapacityContext,
): CapacityAction {
  return hasSelfServeCapacity(subscription)
    ? { label: "Upgrade plan" }
    : { label: "Contact us", href: buildSupportMailtoHref() }
}

// Floored, like the percentage, so the amounts can never read as spent while the
// ratio still says otherwise: 99.96 of 100 is "99.9", not "100".
function formatAmount(value: number): string {
  return (Math.floor(value * 10) / 10).toLocaleString(undefined, {
    maximumFractionDigits: 1,
  })
}

/**
 * Copy for a rung, in one place so the app, and later the API, CLI and email,
 * say the same thing. Nothing here states what happens at the limit: no
 * threshold is enforced from this surface, and the gate's own refusal is what
 * speaks when one is.
 */
export function usageBandCopy(
  state: UsageBandState,
  subscription: CapacityContext,
): { title: string; body: string } {
  const label = DIMENSION_LABEL[state.dimension]
  const amounts = `${formatAmount(state.used)} of ${formatAmount(state.limit)} used this period.`
  const capacity = hasSelfServeCapacity(subscription)
    ? "Upgrade to Growth for more."
    : "Contact us to add capacity."

  if (state.band === "over") {
    return {
      // Exactly spent is the same band as past it — the allowance is gone either
      // way — but only one of them is an overage, so only one of them says so.
      title:
        state.ratio > 1
          ? `You're past your plan's ${label}.`
          : `You've used all of your ${label}.`,
      body: `${amounts} ${capacity}`,
    }
  }
  if (state.band === "nearly") {
    return {
      title: `You've used most of your ${label}.`,
      body: `${amounts} ${capacity}`,
    }
  }
  return {
    title: `You've used ${Math.floor(state.ratio * 100)}% of your ${label}.`,
    body: `${amounts} ${capacity}`,
  }
}
